import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Pagination,
} from '@mui/material';
import ApiService from '../../Api/ApiController';
import Swal from 'sweetalert2';

export default function PaymentSection() {
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [approvedAmount, setApprovedAmount] = useState('');
  const [page, setPage] = useState(1); // current page
  const [totalPages, setTotalPages] = useState(1); // total pages

  useEffect(() => {
    fetchPaymentList(page);
  }, [page]);

  const fetchPaymentList = (pageNumber = 1) => {
    ApiService.paymentlist({ page: pageNumber })
      .then((response) => {
        if (response.data.status === true) {
          setPayments(response.data.data.data);
          setTotalPages(response.data.data.paginator.totalPages || 1);
        
        } else {
          setPayments([]);
          setTotalPages(1);
        }
      })
      .catch((error) => console.error("Error fetching payments:", error));
  };

  const handleApproveClick = (payment) => {
    setSelectedPayment(payment);
    setApprovedAmount(payment.amount);
    setOpen(true);
  };

  const handleRejectClick = (id) => {
    ApiService.paymentStatus({ payment_id: id, status: "reject" })
      .then((response) => {
        if (response.data.status) {
          Swal.fire({
            icon: 'success',
            title: 'Payment Rejected',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
          fetchPaymentList(page);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Failed to Reject Payment',
            text: response.data.message,
          });
        }
      })
      .catch((error) => console.error("Error rejecting payment:", error));
  };

  const handleApproveConfirm = () => {
    if (selectedPayment) {
      ApiService.paymentStatus({
        payment_id: selectedPayment._id,
        status: "approve",
        amount: approvedAmount,
      })
        .then((response) => {
          if (response.data.status) {
            Swal.fire({
              icon: 'success',
              title: 'Payment Approved',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
            });
            fetchPaymentList(page);
            handleModalClose();
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Failed to Approve Payment',
              text: response.data.message,
            });
          }
        })
        .catch((error) => console.error("Error approving payment:", error));
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    setSelectedPayment(null);
    setApprovedAmount('');
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Payment List
      </Typography>
      <List>
        {payments.map((payment) => (
          <ListItem key={payment._id} sx={{ borderBottom: "1px solid #ddd" }}>
            <ListItemText
              primary={payment.user.name}
              secondary={`Amount: ₹${payment.amount} ${payment.utr ? `| UTR: ${payment.utr}` : ''} | Status: ${payment.status}`}
            />
            <ListItemSecondaryAction>
              {payment.screenshot && (
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  sx={{ mr: 1 }}
                  href={`https://api.tradexsecurities.in/uploads/${payment.screenshot}`}
                  target="_blank"
                >
                  View Screenshot
                </Button>
              )}
              {payment.status === "pending" && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleApproveClick(payment)}
                    sx={{ mr: 1 }}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleRejectClick(payment._id)}
                  >
                    Reject
                  </Button>
                </>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* Modal for Approving Payment */}
      <Modal open={open} onClose={handleModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "white",
            p: 3,
            boxShadow: 24,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Approve Payment
          </Typography>
          <TextField
            label="Enter Approved Amount"
            fullWidth
            type="number"
            value={approvedAmount}
            onChange={(e) => setApprovedAmount(e.target.value)}
            margin="normal"
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
            <Button variant="contained" color="primary" onClick={handleApproveConfirm}>
              Confirm
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleModalClose}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
